import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { describeApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1532384748853-8f54a8f476e2?auto=format&fit=crop&w=1600&q=80";

export default function LoginPage() {
    const { login, user } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("coach@demo.com");
    const [password, setPassword] = useState("Coach123!");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (user && user !== false) nav("/"); }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            await login(email, password);
            nav("/");
        } catch (e) {
            setErr(describeApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[var(--bg)]" data-testid="login-page">
            {/* Left visual panel */}
            <div className="relative hidden lg:block overflow-hidden border-r border-[var(--border)]">
                <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a0908] via-[#0a0908]/85 to-transparent" />
                <div className="absolute inset-0 bg-grid opacity-30" />
                <div className="relative z-10 h-full flex flex-col justify-between p-14">
                    <Logo size="md" subtitle />
                    <div>
                        <p className="font-mono text-[10px] tracking-[0.4em] text-[var(--gold)] mb-6">/ DISCIPLINE · DESIGN · DOMINANCE</p>
                        <h1 className="font-serif text-7xl xl:text-8xl leading-[0.95] mb-7 tracking-tight">
                            Coach<br />
                            with <span className="gold-gradient-text italic">precision.</span>
                        </h1>
                        <div className="gold-rule mb-7" />
                        <p className="text-[var(--text-muted)] max-w-md leading-relaxed text-[15px]">
                            Programs, calendars, check-ins, payments, AI insights — one luxury workspace built for elite coaches who refuse to compromise.
                        </p>
                    </div>
                    <div className="flex gap-10 font-mono text-[10px] tracking-[0.3em] text-[var(--text-muted)]">
                        <div><span className="text-[var(--gold)]">01</span> · CLIENTS</div>
                        <div><span className="text-[var(--gold)]">02</span> · PROGRAMS</div>
                        <div><span className="text-[var(--gold)]">03</span> · INSIGHTS</div>
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 lg:p-14 bg-[var(--bg)]">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-12"><Logo size="sm" /></div>
                    <p className="label-tiny mb-3" data-testid="login-eyebrow">/ Sign in</p>
                    <h2 className="font-serif text-5xl mb-2">Welcome back.</h2>
                    <p className="text-[var(--text-muted)] mb-10 text-sm">Continue building champions.</p>

                    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="label-tiny block mb-2">Email</label>
                            <input data-testid="login-email" type="email" className="input-dark" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Password</label>
                            <input data-testid="login-password" type="password" className="input-dark" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                        </div>
                        {err && (
                            <div className="border border-[var(--danger)] bg-[var(--danger)]/10 text-[var(--danger)] px-3 py-2 text-sm" data-testid="login-error">
                                {err}
                            </div>
                        )}
                        <div className="flex items-center justify-between text-xs">
                            <Link to="/forgot-password" className="text-[var(--text-muted)] hover:text-[var(--gold)]" data-testid="goto-forgot">
                                Forgot password?
                            </Link>
                        </div>
                        <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="login-submit">
                            {busy ? "Signing in…" : "Enter Dashboard →"}
                        </button>
                    </form>

                    <div className="gold-rule my-10" />
                    <p className="text-sm text-[var(--text-muted)]">
                        New to NDE?{" "}
                        <Link to="/register" className="text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="goto-register">
                            Create a coach account
                        </Link>
                    </p>
                    <div className="mt-8 p-4 border border-[var(--gold-deep)] bg-[var(--surface)]">
                        <p className="label-tiny mb-2">Demo credentials</p>
                        <p className="font-mono text-xs text-[var(--text-muted)] mb-3">coach@demo.com / Coach123!</p>
                        <button
                            type="button"
                            onClick={() => { setEmail("coach@demo.com"); setPassword("Coach123!"); setErr(""); }}
                            className="btn-ghost w-full text-xs"
                            data-testid="use-demo-creds"
                        >
                            Fill demo credentials
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
